import React from "react";
import { DatePicker, Select } from "antd";
import { type RangePickerProps } from "antd/es/date-picker";
import dayjs from "dayjs";

import i18nextConfig from "~/i18nextConfig";
import { type ComparisonMode } from "~/modules/Dashboard/AppStats/hooks/comparisonRange";
import { type DateRange } from "~/modules/Dashboard/AppStats/hooks/types";
import { useProductsList } from "~/modules/Dashboard/AppStats/hooks/useProductsList";

const rangePresets: RangePickerProps["presets"] = [
  { label: i18nextConfig.t("dashboard:stats.thisMonth"), value: [dayjs().startOf("month"), dayjs()] },
  { label: i18nextConfig.t("dashboard:stats.lastDays", { count: 7 }), value: [dayjs().add(-7, "d"), dayjs()] },
  { label: i18nextConfig.t("dashboard:stats.lastDays", { count: 14 }), value: [dayjs().add(-14, "d"), dayjs()] },
  { label: i18nextConfig.t("dashboard:stats.lastDays", { count: 30 }), value: [dayjs().add(-30, "d"), dayjs()] },
  { label: i18nextConfig.t("dashboard:stats.lastDays", { count: 90 }), value: [dayjs().add(-90, "d"), dayjs()] },
];

export const DEFAULT_STATS_RANGE: DateRange = [dayjs().startOf("month"), dayjs()];

const comparisonModeOptions: { value: ComparisonMode; label: string }[] = [
  { value: "previousMonth", label: i18nextConfig.t("dashboard:stats.compareWith.previousMonth") },
  { value: "previousPeriod", label: i18nextConfig.t("dashboard:stats.compareWith.previousPeriod") },
];

const { RangePicker } = DatePicker;

interface StatsFiltersProps {
  products: string[];
  onProductsChange: (products: string[]) => void;
  range: DateRange;
  onRangeChange: (range: DateRange) => void;
  comparisonMode: ComparisonMode;
  onComparisonModeChange: (mode: ComparisonMode) => void;
}

/** The one filter row every block of the stats dashboard follows. */
export const StatsFilters = ({
  products,
  onProductsChange,
  range,
  onRangeChange,
  comparisonMode,
  onComparisonModeChange,
}: StatsFiltersProps): React.ReactElement => {
  const { productsList } = useProductsList();

  return (
    <div className="flex flex-wrap justify-end gap-3">
      <Select
        mode="multiple"
        onChange={onProductsChange}
        value={products}
        style={{ minWidth: 320, maxWidth: 500 }}
        options={productsList}
      />

      <RangePicker value={range} presets={rangePresets} onChange={(dates) => onRangeChange(dates as DateRange)} />

      <Select<ComparisonMode>
        value={comparisonMode}
        onChange={onComparisonModeChange}
        options={comparisonModeOptions}
        style={{ minWidth: 300 }}
      />
    </div>
  );
};
